import React, { useCallback, useEffect, useState } from "react";
import { Typography } from "@mui/material";
import type { WhenDto } from "../../../../../types/resolver";
import type { AnswerSet, AnswerValue } from "../../../../../types/answerSet";
import type { Question } from "../../../../../types/rubric";
import { fetchAnswerSet, fetchQuestion } from "../../../../../utils/api";
import {
    extractAnswerRefsFromWhen,
    whenToEquation,
    type AnswerDisplayContext,
    type WhenEquationOptions,
} from "./whenToEquation";

function getAnswerDisplayLabel(a: AnswerValue): string {
    return a.displayText ?? a.name ?? a.tagLabel ?? (a.id != null ? String(a.id) : "");
}

export interface WhenVisualizerProps {
    when: WhenDto | null | undefined;
}

/**
 * Renders the current When condition as a single-line equation. Fetches question and answer-set
 * labels so the equation shows values (e.g. answer("Question name") in ['Option A', 'Option B']) instead of IDs.
 */
export const WhenVisualizer: React.FC<WhenVisualizerProps> = ({ when }) => {
    const [questionLabels, setQuestionLabels] = useState<Record<string, string>>({});
    const [answerDisplayByAnswerSetId, setAnswerDisplayByAnswerSetId] = useState<
        Record<string, Record<string, string>>
    >({});
    const [answerDisplayByQuestionId, setAnswerDisplayByQuestionId] = useState<
        Record<string, Record<string, string>>
    >({});

    useEffect(() => {
        if (when == null) {
            setQuestionLabels({});
            setAnswerDisplayByAnswerSetId({});
            setAnswerDisplayByQuestionId({});
            return;
        }
        const refs = extractAnswerRefsFromWhen(when);
        const questionIds = [...new Set(refs.map((r) => r.questionId).filter((id): id is number | string => id != null))];
        const answerSetIds = [
            ...new Set(
                refs.map((r) => r.answerSetId).filter((id): id is string | number => id != null && id !== "")
            ),
        ];

        let cancelled = false;

        const loadQuestion = async (qId: number | string): Promise<{ qId: string; label: string; answersByValueId: Record<string, string> } | null> => {
            const id = typeof qId === "string" ? parseInt(qId, 10) : qId;
            if (!Number.isFinite(id)) return null;
            try {
                const q = await fetchQuestion(id);
                if (cancelled) return null;
                const answers = q.answers ?? (q.answerSetId != null ? (await fetchAnswerSet(Number(q.answerSetId))).answers : undefined);
                const answersByValueId: Record<string, string> = {};
                (answers ?? []).forEach((a) => {
                    if (a.id != null) answersByValueId[String(a.id)] = getAnswerDisplayLabel(a);
                });
                return {
                    qId: String(qId),
                    label: q.name || q.questionText || `Question ${id}`,
                    answersByValueId,
                };
            } catch {
                return null;
            }
        };

        const loadAnswerSet = async (asId: string | number): Promise<{ asId: string; answersByValueId: Record<string, string> } | null> => {
            const id = typeof asId === "string" ? parseInt(asId, 10) : asId;
            if (!Number.isFinite(id)) return null;
            try {
                const set = await fetchAnswerSet(id);
                if (cancelled) return null;
                const answersByValueId: Record<string, string> = {};
                (set.answers ?? []).forEach((a) => {
                    if (a.id != null) answersByValueId[String(a.id)] = getAnswerDisplayLabel(a);
                });
                return { asId: String(asId), answersByValueId };
            } catch {
                return null;
            }
        };

        Promise.all([
            ...questionIds.map(loadQuestion),
            ...answerSetIds.map(loadAnswerSet),
        ]).then((results) => {
            if (cancelled) return;
            const qLabels: Record<string, string> = {};
            const byQuestion: Record<string, Record<string, string>> = {};
            const byAnswerSet: Record<string, Record<string, string>> = {};
            results.forEach((r) => {
                if (!r) return;
                if ("label" in r) {
                    qLabels[r.qId] = r.label;
                    byQuestion[r.qId] = r.answersByValueId;
                } else {
                    byAnswerSet[r.asId] = r.answersByValueId;
                }
            });
            setQuestionLabels(qLabels);
            setAnswerDisplayByQuestionId(byQuestion);
            setAnswerDisplayByAnswerSetId(byAnswerSet);
        });

        return () => {
            cancelled = true;
        };
    }, [when]);

    const getQuestionLabel = useCallback(
        (questionId: number | string): string | undefined => questionLabels[String(questionId)],
        [questionLabels]
    );

    const getAnswerDisplayText = useCallback(
        (valueId: unknown, context: AnswerDisplayContext): string | undefined => {
            const id = valueId == null ? undefined : String(valueId);
            if (id == null) return undefined;
            if (context.answerSetId != null) {
                const bySet = answerDisplayByAnswerSetId[String(context.answerSetId)];
                if (bySet?.[id] != null) return bySet[id];
            }
            if (context.questionId != null) {
                const byQ = answerDisplayByQuestionId[String(context.questionId)];
                if (byQ?.[id] != null) return byQ[id];
            }
            return undefined;
        },
        [answerDisplayByAnswerSetId, answerDisplayByQuestionId]
    );

    const options: WhenEquationOptions = {
        getQuestionLabel,
        getAnswerDisplayText,
    };

    const equation = whenToEquation(when, options);

    return (
        <Typography
            component="span"
            variant="body2"
            sx={{
                display: "block",
                fontFamily: "monospace",
                fontSize: "0.8125rem",
                py: 0.5,
                px: 1,
                borderRadius: 1,
                bgcolor: "action.hover",
                wordBreak: "break-word",
            }}
        >
            {equation}
        </Typography>
    );
};
